import { toast } from "vue-sonner";
import { getValidationErrorMessage } from "../../services/validationMessages.js";

/**
 * Composable for handling UI notifications with consistent messaging
 * Abstracts toast notifications and provides standardized messages
 */
export function useNotificationService() {
	const showSuccessToast = (message) => {
		toast.success(message);
	};

	const showErrorToast = (message) => {
		toast.error(message);
	};

	const showLoadingToast = (promise, messages) => {
		return toast.promise(promise, messages);
	};

	// Booking operation messages
	const getBookingMessages = (operation, bookingTitle = "Buchung") => {
		const messages = {
			create: {
				loading: "Buchung wird erstellt...",
				success: `Buchung "${bookingTitle}" erfolgreich erstellt`,
				error: "Fehler beim Erstellen der Buchung",
			},
			"create-walkin": {
				loading: "Sofortbuchung wird erstellt...",
				success: "Sofortbuchung erfolgreich erstellt",
				error: "Fehler beim Erstellen der Sofortbuchung",
			},
			"end-walkin": {
				loading: "Sofortbuchung wird beendet...",
				success: "Sofortbuchung erfolgreich beendet",
				error: "Fehler beim Beenden der Sofortbuchung",
			},
			update: {
				loading: "Buchung wird aktualisiert...",
				success: `Buchung "${bookingTitle}" erfolgreich aktualisiert`,
				error: "Fehler beim Aktualisieren der Buchung",
			},
			delete: {
				loading: "Buchung wird gelöscht...",
				success: "Buchung erfolgreich gelöscht",
				error: "Fehler beim Löschen der Buchung",
			},
		};
		return messages[operation] || messages.update;
	};

	const getSeriesMessages = (operation, seriesTitle = "Serie") => {
		const messages = {
			create: {
				loading: "Serie wird erstellt...",
				success: `Serie "${seriesTitle}" erfolgreich erstellt`,
				error: "Fehler beim Erstellen der Serie",
			},
			update: {
				loading: "Serie wird aktualisiert...",
				success: `Serie "${seriesTitle}" erfolgreich aktualisiert`,
				error: "Fehler beim Aktualisieren der Serie",
			},
			delete: {
				loading: "Serie wird gelöscht...",
				success: "Serie erfolgreich gelöscht",
				error: "Fehler beim Löschen der Serie",
			},
		};
		return messages[operation] || messages.update;
	};

	const getFetchMessages = (isInitialLoad = false, count = 0) => {
		return {
			loading: isInitialLoad
				? "Raumbuchungen werden geladen..."
				: "Ereignisse werden geladen...",
			success: isInitialLoad
				? `${count} Buchung${count !== 1 ? "en" : ""} geladen`
				: `Kalender mit ${count} Buchung${count !== 1 ? "en" : ""} aktualisiert`,
			error: "Fehler beim Laden der Buchungen",
		};
	};

	// Specific notification methods
	const notifyFetchSuccess = (count, isInitialLoad = false) => {
		const message = isInitialLoad
			? `${count} Buchung${count !== 1 ? "en" : ""} geladen`
			: `Kalender mit ${count} Buchung${count !== 1 ? "en" : ""} aktualisiert`;
		showSuccessToast(message);
	};

	const notifyFetchError = (error) => {
		const message = error?.message || "Fehler beim Laden der Buchungen";
		showErrorToast(message);
	};

	const notifyBookingSuccess = (operation, title = "Buchung") => {
		const messages = getBookingMessages(operation, title);
		showSuccessToast(messages.success);
	};

	const notifyBookingError = (operation, error) => {
		const messages = getBookingMessages(operation);
		const message = error?.message || messages.error;
		showErrorToast(message);
	};

	const notifySeriesSuccess = (operation, title = "Serie") => {
		const messages = getSeriesMessages(operation, title);
		showSuccessToast(messages.success);
	};

	const notifySeriesError = (operation, error) => {
		const messages = getSeriesMessages(operation);
		const message = error?.message || messages.error;
		showErrorToast(message);
	};

	// Simple drag/drop notifications
	const notifyDragDropSuccess = () => {
		showSuccessToast("Buchung aktualisiert");
	};

	const notifyDragDropError = (error) => {
		const message = error?.message || "Aktualisierung fehlgeschlagen";
		showErrorToast(message);
	};

	// Validation error notification - the server only reports which rules failed, so the
	// German wording comes from the central mapping instead of from the response.
	const notifyValidationError = (error) => {
		showErrorToast(getValidationErrorMessage(error?.validationMessages));
	};

	return {
		// Basic toast methods
		showSuccessToast,
		showErrorToast,
		showLoadingToast,

		// Message generators
		getBookingMessages,
		getSeriesMessages,
		getFetchMessages,

		// Specific notification methods
		notifyFetchSuccess,
		notifyFetchError,
		notifyBookingSuccess,
		notifyBookingError,
		notifySeriesSuccess,
		notifySeriesError,
		notifyDragDropSuccess,
		notifyDragDropError,
		notifyValidationError,
	};
}